import * as React from 'react';
import styled from 'styled-components';
import { FixMe } from '@utils/types';
import TextInputField, { FORM_FIELD_ERROR_CLASS } from '@components/common/textInput/textInput';
import { SwitchInput } from './switchInput';
import { DynamicForm } from './dynamicForm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import { SingleSelect } from '@components/common/formGroup/singleSelect';
import { getFieldConfigs } from '@components/common/formGroup/utils';
import { LargeText } from '@ui-library/typography';
import { Button } from '@ui-library/button';
import { RedAsterisk } from '@components/common/redAsterisk';

interface IDynamicCustomFormProps {
  field: FixMe;
  onChange: FixMe;
  disabled: boolean;
  // Set by a parent when this sub-form is a required field left empty: surface its required-field
  // errors even before a row is touched, so an empty required sub-form highlights its inputs.
  forceRequiredErrors?: boolean;
}
interface IDynamicCustomFormState {
  mapping: FixMe;
  rowKeys: string[];
}

const getDefaultValue = (field: FixMe) => {
  const fieldType = field.type;
  switch (fieldType) {
    case 'singleSelect':
    case 'textInput':
    case 'textareaInput':
      return '';
    case 'checkbox':
      return false;
    case 'dynamicForm': {
      const { keyLeft } = field;
      const { keyRight } = field;
      return [
        {
          [keyLeft]: '',
          [keyRight]: '',
        },
      ];
    }
    case 'dynamicCustomForm':
      return [];
  }
  return undefined;
};

// The UI config ships a pre-filled display template as `field.default` (e.g. consent management's
// `[{ resolutionStrategy: '', consents: [{ consent: '' }] }]`). That template must seed the form for
// editing but must NOT be persisted as a real value — the backend rejects the empty leaves. This
// recursively reports whether a value carries any user-supplied content so empty template rows can
// be dropped before persisting, mirroring the new form builder's `[]` behaviour.
const isMeaningfulValue = (value: FixMe): boolean => {
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === 'string') {
    return value.trim() !== '';
  }
  if (typeof value === 'boolean') {
    return value;
  }
  if (Array.isArray(value)) {
    return value.some(isMeaningfulValue);
  }
  if (typeof value === 'object') {
    return Object.values(value).some(isMeaningfulValue);
  }
  return true;
};

const isRowNonEmpty = (row: FixMe) => isMeaningfulValue(row);

// Whether a control in a row carries user content (type-aware). Checkboxes never count as data.
const isCustomFormFieldFilled = (customField: FixMe, row: FixMe): boolean => {
  const value = row?.[customField.value];
  switch (customField.type) {
    case 'checkbox':
      return false;
    case 'dynamicForm':
      return (
        Array.isArray(value) &&
        value.some((entry: FixMe) =>
          Object.values(entry || {}).some((cell) => String(cell ?? '').trim() !== ''),
        )
      );
    case 'dynamicCustomForm':
      return (
        Array.isArray(value) &&
        value.some((nestedRow: FixMe) =>
          (customField.customFields || []).some((nestedField: FixMe) =>
            isCustomFormFieldFilled(nestedField, nestedRow),
          ),
        )
      );
    default:
      return String(value ?? '').trim() !== '';
  }
};

// The default Select/Input variants ship no error-border CSS, so an empty required control renders
// no red border. Force it; !important is needed to beat Ant's themed selector border on specificity.
const FormContainer = styled.div`
  .ant-select-status-error .ant-select-selector {
    border-color: ${({ theme }) => theme.token.colorError} !important;
  }

  .${FORM_FIELD_ERROR_CLASS} {
    border-color: ${({ theme }) => theme.token.colorError} !important;
  }
`;

/* eslint-disable import/no-default-export */
export default class DynamicCustomForm extends React.Component<
  IDynamicCustomFormProps,
  IDynamicCustomFormState
> {
  // Stable per-row keys so deleting a block unmounts that block's stateful controls (e.g. the
  // single select holds its value in local state) instead of feeding shifted data to a retained
  // instance, which would leave a stale value behind.
  private nextRowKey = 0;

  private createRowKey = () => {
    this.nextRowKey += 1;
    return `dcf-row-${this.nextRowKey}`;
  };

  // A single empty row built from each control's configured default (or its type fallback). A
  // required control is never seeded from its configured default — it starts empty so an untouched
  // block shows no required errors (mirrors the new form builder).
  private buildDefaultRow = () => {
    const fieldData = new Map();
    this.props.field.customFields.forEach((field: FixMe) => {
      const seededValue = field.required || !field.default ? getDefaultValue(field) : field.default;
      fieldData.set(field.value, seededValue);
    });
    return Object.fromEntries(fieldData);
  };

  // A row still structurally equal to a freshly-built default row carries no user input — its
  // non-required controls may hold seeded scalar defaults (e.g. a resolutionStrategy default). Treat
  // it as empty so an untouched or cleared block persists as `[]` rather than a phantom default row.
  private isDefaultRow = (row: FixMe) =>
    JSON.stringify(row) === JSON.stringify(this.buildDefaultRow());

  // A row is persisted only when it carries real user content: non-empty and not merely the seed.
  private isPersistableRow = (row: FixMe) => isRowNonEmpty(row) && !this.isDefaultRow(row);

  constructor(props: IDynamicCustomFormProps) {
    super(props);
    // Seed from the saved template when present so controls render the saved values; otherwise a
    // single empty default row. `mapping` is the source of truth the fields read their values from.
    const seededRows =
      props.field.default && props.field.default.length > 0
        ? [...props.field.default]
        : [this.buildDefaultRow()];
    this.state = {
      mapping: seededRows,
      rowKeys: seededRows.map(() => this.createRowKey()),
    };
  }

  componentDidMount() {
    const { field, onChange } = this.props;
    if (field.default && field.default.length > 0) {
      // mapping/rowKeys are seeded from field.default in the constructor; here we only persist the
      // rows that carry real content so a purely-empty template collapses to `[]`.
      onChange(field.value, field.default.filter(isRowNonEmpty));
    }
  }

  public onConfigChange = (index: number, value: string, keyValue: string) => {
    const { onChange, field } = this.props;
    this.setState(
      (prevState: FixMe) => {
        const mapping = prevState.mapping.slice();
        const updatedItem = {
          ...mapping[index],
          [keyValue]: value,
        };
        mapping.splice(index, 1, updatedItem);
        return {
          ...prevState,
          mapping,
        };
      },
      () => {
        const { mapping } = this.state;
        // Drop rows the user hasn't filled — including multi-field template rows like
        // `{ resolutionStrategy: '', consents: [] }` and seeded default rows. This keeps the
        // persisted value as `[]` until a row carries real content.
        const trimmedMapping = mapping.filter(this.isPersistableRow);
        onChange(field.value, trimmedMapping);
      },
    );
  };

  // The `value`s of visible required controls left empty in a touched row (data in >=1 visible
  // non-checkbox field). An untouched row reports nothing, so a fresh block highlights no errors.
  private getRowRequiredErrors = (index: number): string[] => {
    const row = this.state.mapping[index];
    if (!row) {
      return [];
    }
    const visibleFields = this.props.field.customFields.filter(
      (customField: FixMe) => getFieldConfigs(customField, row) !== null,
    );
    const dataFields = visibleFields.filter(
      (customField: FixMe) => customField.type !== 'checkbox',
    );
    const rowHasData = dataFields.some((customField: FixMe) =>
      isCustomFormFieldFilled(customField, row),
    );
    // A required sub-form forced into error by its parent must surface its (now all-required)
    // children even before the user types into a row.
    if (!rowHasData && !this.props.forceRequiredErrors) {
      return [];
    }
    return dataFields
      .filter(
        (customField: FixMe) => customField.required && !isCustomFormFieldFilled(customField, row),
      )
      .map((customField: FixMe) => customField.value);
  };

  private renderSingleRow = (index: number, rowKey: string) => {
    const { field, disabled } = this.props;
    const requiredErrors = this.getRowRequiredErrors(index);

    return (
      <div key={rowKey}>
        {!disabled && (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              size="small"
              type="text"
              onClick={() => this.removeItem(index)}
              icon={<FontAwesomeIcon icon={regular('trash')} />}
              aria-label="Delete block"
            />
          </div>
        )}
        {field.customFields.map((customField: FixMe) =>
          this.renderField({ ...customField }, index, rowKey, requiredErrors),
        )}
      </div>
    );
  };

  handleAddMore = () => {
    // Grow `mapping` and `rowKeys` in lockstep so they never diverge — controls read/write
    // `mapping[index]` while render maps over `rowKeys` by index, so a length mismatch would let
    // `onConfigChange`'s `splice` clamp an out-of-range index and route a value to the wrong block.
    this.setState((prevState: FixMe) => ({
      mapping: [...prevState.mapping, this.buildDefaultRow()],
      rowKeys: [...prevState.rowKeys, this.createRowKey()],
    }));
  };

  // Remove the block at `index`. Splicing both `mapping` and `rowKeys` keeps them aligned, and the
  // stable keys ensure the surviving blocks keep their controls (and values) rather than inheriting
  // shifted data. Always keep at least one block: deleting the last one resets it to a fresh empty
  // row (cleared values) — with a new key so the controls remount — rather than removing it.
  private removeItem = (index: number) => {
    this.setState(
      (prevState: FixMe) => {
        const mapping = [...prevState.mapping];
        if (index < mapping.length) {
          mapping.splice(index, 1);
        }
        const rowKeys = [...prevState.rowKeys];
        rowKeys.splice(index, 1);
        if (rowKeys.length === 0) {
          mapping.push(this.buildDefaultRow());
          rowKeys.push(this.createRowKey());
        }
        return { ...prevState, mapping, rowKeys };
      },
      () => {
        const { onChange, field } = this.props;
        onChange(field.value, this.state.mapping.filter(this.isPersistableRow));
      },
    );
  };

  public render() {
    const { field, disabled } = this.props;
    const { rowKeys } = this.state;
    return (
      <FormContainer data-testid="dynamicCustomForm">
        {field.label && (
          <LargeText>
            {field.label}
            {field.required && <RedAsterisk />}
          </LargeText>
        )}
        {rowKeys.map((rowKey: string, index: number) => this.renderSingleRow(index, rowKey))}
        {!disabled && (
          <Button
            noPadding
            type="link"
            onClick={this.handleAddMore}
            icon={<FontAwesomeIcon icon={regular('circle-plus')} />}
          >
            Add more
          </Button>
        )}
      </FormContainer>
    );
  }

  private renderField = (
    customField: FixMe,
    index: FixMe,
    rowKey: string,
    requiredErrors: string[],
  ) => {
    const { disabled } = this.props;
    const formRowData = this.state.mapping[index];

    const fieldConfig = getFieldConfigs(customField, formRowData);
    if (fieldConfig === null) {
      return null;
    }

    // Seed each control from the live row data (mapping), not the static field.default template, so
    // values stay correct after a block is deleted/reordered and a re-seeded block reads as empty.
    if (formRowData && formRowData[customField.value] !== undefined) {
      customField.default = formRowData[customField.value];
    } else {
      customField.default = getDefaultValue(customField);
    }

    // A required single-select must not auto-apply its configured default option — it starts
    // unselected so an untouched block shows no error (the seed above leaves it empty).
    if (customField.type === 'singleSelect' && customField.required) {
      customField.defaultOption = undefined;
    }

    const isError = requiredErrors.includes(customField.value);

    switch (customField.type) {
      case 'textInput':
      case 'textareaInput':
        return (
          <div className="p-b-sm" key={customField.label + rowKey}>
            <TextInputField
              field={customField}
              isError={isError}
              onChange={(label, value) => {
                this.onConfigChange(index, value, label);
              }}
              type={customField.type === 'textInput' ? 'input' : 'textarea'}
              disabled={disabled}
            />
            {customField.footerNote && (
              <div className="p-t-sm p-b-sm">{customField.footerNote}</div>
            )}
          </div>
        );

      case 'checkbox':
        return (
          <div className="p-b-sm" key={customField.label + rowKey}>
            <SwitchInput
              field={customField}
              hidden={false}
              onChange={(label, value) => {
                this.onConfigChange(index, value, label);
              }}
              disabled={disabled}
            />
            {customField.footerNote && (
              <div className="p-t-sm p-b-sm">{customField.footerNote}</div>
            )}
          </div>
        );

      case 'dynamicForm':
        return (
          <div className="p-b-sm" key={customField.label + rowKey}>
            <DynamicForm
              field={customField}
              onChange={(label: FixMe, value: FixMe) => {
                this.onConfigChange(index, value, label);
              }}
              disabled={disabled}
              secret={[]}
            />
            {customField.footerNote && (
              <div className="p-t-sm p-b-sm">{customField.footerNote}</div>
            )}
          </div>
        );

      case 'singleSelect':
        return (
          <div
            className="p-b-sm form_group__select_container"
            data-testid="fgsingleSelect"
            key={customField.label + rowKey}
          >
            <SingleSelect
              field={customField}
              isError={isError}
              onChange={(label, value) => {
                this.onConfigChange(index, value, label);
              }}
              disabled={disabled}
            />
            {customField.footerNote && (
              <div className="p-t-sm p-b-sm">{customField.footerNote}</div>
            )}
          </div>
        );

      case 'dynamicCustomForm': {
        // A required sub-form makes all its children required too, and (via forceRequiredErrors)
        // surfaces those required errors even before a row is touched.
        const nestedField = customField.required
          ? {
              ...customField,
              customFields: (customField.customFields || []).map((child: FixMe) => ({
                ...child,
                required: true,
              })),
            }
          : customField;
        return (
          <div
            className="p-l-lg"
            data-testid="nestedDynamicCustomForm"
            key={customField.label + rowKey}
          >
            <DynamicCustomForm
              field={nestedField}
              forceRequiredErrors={isError}
              onChange={(label: string, value: string) => {
                this.onConfigChange(index, value, label);
              }}
              disabled={disabled}
            />
            {customField.footerNote && (
              <div className="p-t-sm p-b-sm">{customField.footerNote}</div>
            )}
          </div>
        );
      }

      default:
        break;
    }

    return null;
  };
}
